import { useEffect, useMemo } from 'react';
import {
  fullWidthClassName,
  noScrollbarsClassName,
  removedBarSizeVariable,
  zeroRightClassName,
} from './constants';

/**
 * Ersatz für `react-remove-scroll-bar` (Alias in `vite.config.ts` und `vitest.config.ts`): sperrt das Scrollen der Seite,
 * solange ein Dialog oder Menü offen ist, **ohne** ein `<style>` in den Kopf der Seite zu schreiben. Die Bibliothek tut
 * das beim Öffnen und verletzt damit die Content-Security-Policy der Auslieferung (`style-src 'self'`). Hier setzt die
 * Komponente nur das Attribut `data-scroll-locked` (Zähler für verschachtelte Sperren) und beim ersten Sperren die Breite
 * der Bildlaufleiste als Eigenschaft über die Objektschnittstelle des Stils (von der CSP nicht berührt); die Regeln dazu
 * stehen in `scroll-lock.css`.
 */
export { fullWidthClassName, noScrollbarsClassName, removedBarSizeVariable, zeroRightClassName };

const LOCK = 'data-scroll-locked';

export type RemoveScrollBarProps = {
  noRelative?: boolean;
  noImportant?: boolean;
  gapMode?: 'margin' | 'padding';
};

/** Breite der Bildlaufleiste: Fensterbreite minus Breite des Dokuments (0 ohne Bildlaufleiste, z. B. auf dem Handy). */
export function getGapWidth(): { left: number; top: number; right: number; gap: number } {
  if (typeof window === 'undefined') {
    return { left: 0, top: 0, right: 0, gap: 0 };
  }
  return {
    left: 0,
    top: 0,
    right: 0,
    gap: Math.max(0, window.innerWidth - document.documentElement.clientWidth),
  };
}

const counter = (): number => {
  const value = Number.parseInt(document.body.getAttribute(LOCK) ?? '0', 10);
  return Number.isFinite(value) ? value : 0;
};

export function RemoveScrollBar(_props: RemoveScrollBarProps) {
  // Gemessen wird vor dem Sperren (danach wäre die Leiste schon weg); nur die erste Sperre setzt die Breite.
  const gap = useMemo(() => (counter() === 0 ? getGapWidth().gap : 0), []);
  useEffect(() => {
    const first = counter() === 0;
    document.body.setAttribute(LOCK, String(counter() + 1));
    if (first) {
      document.body.style.setProperty(removedBarSizeVariable, `${gap}px`);
    }
    return () => {
      const rest = counter() - 1;
      if (rest <= 0) {
        document.body.removeAttribute(LOCK);
        document.body.style.removeProperty(removedBarSizeVariable);
      } else {
        document.body.setAttribute(LOCK, String(rest));
      }
    };
  }, [gap]);
  return null;
}
